import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Hook compartido por las pantallas del Portal del Cliente Prendas.
 *
 * Mantiene el cliente activo (cuando el usuario cliente_prendas tiene varios
 * clientes asignados) y lo persiste en localStorage para que la selección
 * se conserve al navegar entre rutas (/mis-prendas, /mis-prendas/recolecciones,
 * /mis-prendas/entregas, /mis-prendas/balance) o recargar la página.
 *
 * Uso:
 *   const {
 *     clientesAsignados,   // array completo [{ id, nombre, nit? }]
 *     clienteActivo,       // objeto del cliente activo o null
 *     clienteActivoId,
 *     clienteActivoNombre,
 *     clienteActivoNit,
 *     setClienteActivo,    // (id) => void
 *     tieneVarios,         // boolean
 *   } = useClienteActivo();
 */
const STORAGE_KEY = 'clientePrendas.activoId';

const safeGetStored = () => {
  try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
};
const safeSetStored = (id) => {
  try {
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);
  } catch { /* ignore */ }
};

const useClienteActivo = () => {
  const { user } = useAuth();
  const clientesAsignados = useMemo(
    () => (Array.isArray(user?.raw?.clientesAsignados) ? user.raw.clientesAsignados : []),
    [user?.raw?.clientesAsignados],
  );

  const [activoId, setActivoId] = useState(() => {
    const stored = safeGetStored();
    // Solo es válido al inicio si está en la lista; la validación
    // definitiva ocurre en el effect siguiente.
    return stored;
  });

  // Sincroniza el activoId con la lista real de clientes asignados:
  //  - si no hay clientes -> null
  //  - si el activo guardado no está en la lista -> cae al primero
  useEffect(() => {
    if (clientesAsignados.length === 0) {
      if (activoId !== null) {
        setActivoId(null);
        safeSetStored(null);
      }
      return;
    }
    const existe = clientesAsignados.some((c) => c.id === activoId);
    if (!existe) {
      const firstId = clientesAsignados[0].id;
      setActivoId(firstId);
      safeSetStored(firstId);
    }
  }, [clientesAsignados, activoId]);

  const setClienteActivo = useCallback((id) => {
    setActivoId(id);
    safeSetStored(id);
  }, []);

  const clienteActivo = useMemo(() => {
    if (!activoId) return null;
    return clientesAsignados.find((c) => c.id === activoId) || null;
  }, [activoId, clientesAsignados]);

  return {
    clientesAsignados,
    clienteActivo,
    clienteActivoId: clienteActivo?.id || null,
    clienteActivoNombre: clienteActivo?.nombre || '',
    clienteActivoNit: clienteActivo?.nit || null,
    setClienteActivo,
    tieneVarios: clientesAsignados.length > 1,
  };
};

export default useClienteActivo;
